/**
 * 31 - Partisan Rendezvous (10 §2): the radio set rides down in pod A and weighs 8; carry it to the
 * partisans' camp in the trees north of the belt and hand it over. Lose pod A - smash it on bad ground, or
 * never kick it out - and the mission is lost at the drop: the flyover matters. The enemy holds the ground
 * in between, but not the camp: nothing is posted in it and no mine or wire is laid near it.
 *
 * ENGINE GAP, not faked: the contract has no non-hostile figure - every spawn is an enemy, and a captive is
 * someone to be freed and led away - so the camp is tents, stores and a marker, with nobody standing in it.
 * See the GEN3b report: the partisans themselves want a `friendly` figure from the engine.
 */

import { cos, sin, TAU } from '../../sim/math';
import type { PropDef } from '../../sim/types';
import { checker, insideRect } from './helpers';
import { clearing, flagTargets, keepClear, RECALL } from './helpers3b';
import type { ArchetypeSpec } from './types';

const ITEM = { name: 'RADIO SET', weight: 8, pod: 0 } as const;
/** The camp's clearing, and how far round it the garrison keeps away: a soldier sees 22 m by day. */
const CAMP_RADIUS = 7;
const NOBODY_WITHIN = 12;

export const partisanRendezvous: ArchetypeSpec = {
  id: 'partisan-rendezvous',
  name: 'PARTISAN RENDEZVOUS',
  nativeBiome: 'temperate',
  baseThreat: 3,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 54], d: [28, 32] },
  river: false,
  // Nothing of the enemy's is inside: his patrol walks round the wood, not through it.
  patrolLoop: 'outside',
  breach: { min: 0, max: 0 },

  build(g) {
    const at = clearing(g, { within: g.rect, radius: CAMP_RADIUS });
    if (!at) g.map.problems.push('partisan-rendezvous: no clearing for the camp');
    const where = at ?? { x: (g.rect.x0 + g.rect.x1) / 2, z: (g.rect.z0 + g.rect.z1) / 2 };
    const camp: PropDef = g.map.add('partisanCamp', where.x, where.z);
    // Two tents and their stores round the fire, the opening to the south where he comes in from.
    g.map.place('tent', where.x - 4.2, where.z - 2.5, { margin: 0.3 });
    g.map.place('tent', where.x + 4.2, where.z - 2.5, { margin: 0.3 });
    g.map.place('crate', where.x - 1.2, where.z - 4.6, { margin: 0.2 });
    g.map.place('crate', where.x + 1.4, where.z - 4.9, { margin: 0.2 });
    keepClear(g, where, NOBODY_WITHIN);

    // The wood that hides it: the theatre's own tall growth in a ring, whatever fits.
    const [tall] = g.kit.tall;
    for (let i = 0; tall && i < 14; i++) {
      const a = (TAU * i) / 14 + g.rng.range(-0.15, 0.15);
      const d = NOBODY_WITHIN + g.rng.range(1.5, 6);
      g.map.place(tall.kind, where.x + sin(a) * d, where.z + cos(a) * d, {
        scale: g.rng.range(tall.scale[0], tall.scale[1]), yaw: g.rng.range(0, TAU), margin: 0.6,
      });
    }

    return {
      objective: {
        kind: 'deliverItem', label: 'DELIVER THE RADIO SET', targetPropIds: [camp.id], minFraction: 1,
        questItem: { ...ITEM }, deliverToPropId: camp.id, useLabel: 'HAND OVER THE RADIO SET',
        rules: [RECALL],
      },
      finish(def) {
        flagTargets(def, [camp.id]);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('partisan-rendezvous');
    const o = def.objective;
    check(o.kind === 'deliverItem', 'objective must be deliverItem');
    check(
      o.questItem?.name === ITEM.name && o.questItem.weight === ITEM.weight && o.questItem.pod === ITEM.pod,
      'the radio set must ride in pod A and weigh 8',
    );
    const camp = def.props.find((p) => p.id === o.deliverToPropId);
    check(camp?.kind === 'partisanCamp' && def.props.filter((p) => p.kind === 'partisanCamp').length === 1, 'needs exactly one camp to hand over at');
    check(o.targetPropIds.length === 1 && o.targetPropIds[0] === camp?.id && camp?.target === true, 'the camp must be marked on the strip map');
    if (camp) {
      check(insideRect(def.goalZone, camp.x, camp.z), 'the camp must lie in the goal zone');
      check(probe.breach(camp.id) === 0, 'the camp must be reachable without crossing mines or wire');
      const r2 = NOBODY_WITHIN * NOBODY_WITHIN;
      const posted = def.spawns.filter((s) => s.dormantUntil === undefined && s.patrol === undefined
        && (s.x - camp.x) * (s.x - camp.x) + (s.z - camp.z) * (s.z - camp.z) < r2);
      check(posted.length === 0, 'nobody of the enemy is posted in the partisans\' camp');
      const mined = def.mineFields.some((f) => camp.x >= f.rect.x0 - 6 && camp.x <= f.rect.x1 + 6 && camp.z >= f.rect.z0 - 6 && camp.z <= f.rect.z1 + 6);
      check(!mined, 'no minefield is laid at the camp');
    }
    // The contract has no friendly figure: until it has, nobody may be dressed up as one.
    check(def.captives.length === 0, 'the camp holds no captives: partisans are not people to be freed');
    return out;
  },
};
